import Vue from 'vue'
import {ActionTree, MutationTree} from 'vuex'
import {RootState} from '@/store/types'

import {Show} from '@/store/modules/lib'
import axios from 'axios';
import alerts from '@/store/modules/alerts';
import i18n from "@/helpers/i18n";

export interface Company {
  id: string,
  title: string,
  _show: string,
  description: string | null,
  contacts: any[],
  locations: any[],
  eventCode: string,
  showCode: string,
  eventTitle: string,
  showTitle: string,
  standTitleObjects:object,
  accountExists: boolean
}

export interface State {
  company:Company | null ,
  loading:boolean,
}

const defaultState: State = {
  company:null,
  loading:false,
};

const getters = {
  loading: (state: State) => state.loading,
  company: (state: State) => state.company,
}

const mutations: MutationTree<State> = {
  setCompany(state: State, payload: Company) {
    payload.locations.forEach((location: any) => {
      payload.contacts.push(
        {...location,
        type: 'address',
        value: location.address
      }
      )
    })
    payload.contacts = payload.contacts.map((contact: any) => {
      return {...contact, idType: contact.id + contact.type}
    })
    state.company = payload
  },
  setLoadStatus(state: State, payload: boolean) {
    state.loading = payload
  },
  setAccountExists(state: State) {
    if(state.company) state.company.accountExists = true
  }
}

const actions: ActionTree<State, RootState> = {
  async loadCompanyFromServer({commit, state},payload:string) {
    commit('setLoadStatus', true)
    try {
      const {data} = await axios.get('/v4/exhibitor/'+payload+'?expand=stands,eventCode,showCode,showTitle,eventTitle,tariff,accountExists')
      if(data.status === 'err' && data.errors.length){
        this.commit('alerts/setAlert', {
          show: true,
          messages: data.errors[0].messages
        })
      }
      commit('setCompany', data)
      commit('setLoadStatus', false)
    } catch (e) {
      console.warn(e.message)
      commit('setLoadStatus', false)
    }
  },
  async saveCompanyFromServer({commit, state},payload:any){
    const id = payload.id
    const params = {
      data: {
        description: payload.description
      }
    }

    try {
      const {data} = await axios.patch('v4/show/company/' + id + '?expand=stands,eventCode,showCode,showTitle,eventTitle,tariff,accountExists', params)
      commit('setCompany', data.data)
      this.commit('alerts/setAlert', {
        show: true,
        color: 'green',
        messages: [i18n.t('company.notifications.save_success')]
      })
      return true
    }catch (e) {
      return false

    }
  },
  async saveContactsFromServer({commit, state},payload:any){
    if(state.company){
      const id = state.company.id
      const map:any[] = []
      state.company.contacts.forEach((contact:any)=>{
        if(contact.id === payload.id) {
          map.push(payload)
        } else if (contact.type != 'address') {
          map.push(contact)
        }
      })
      const params = {
        data: {
          contacts: map
        }
      }
      try {
        const {data} = await axios.patch('v4/show/company/' + id + '?expand=stands,eventCode,showCode,showTitle,eventTitle,tariff', params)
        commit('setCompany', data.data)
        this.commit('dialog/SetFullDialogShow', false)
        this.commit('alerts/setAlert', {
          show: true,
          color: 'green',
          messages: [i18n.t('company.notifications.save_success')]
        })
        return true
      }catch (e) {
        return false
      }
    }
  },
  async saveAddressFromServer({commit, state},payload:any){
    if(state.company){
      const id = state.company.id
      const map:any[] = state.company.locations.map((el:Company)=>{
        if(el.id == payload.id) return payload
        return el
      })
      const params = {
        data: {
          locations: map
        }
      }
      try {
        const {data} = await axios.patch('v4/show/company/' + id + '?expand=stands,eventCode,showCode,showTitle,eventTitle,tariff', params)
        commit('setCompany', data.data)
        this.commit('dialog/SetFullDialogShow', false)
        this.commit('alerts/setAlert', {
          show: true,
          color: 'green',
          messages: [i18n.t('company.notifications.save_success')]
        })
        return true
      }catch (e) {
        return false
      }
    }
  },
  async deleteContactsFromServer({commit, state},payload:any){
    if(state.company){
      const id = state.company.id
      const map:any[] = []
      state.company.contacts.forEach((contact:any)=>{
        if(contact.id != payload && contact.type != 'address') {
          map.push(contact)
        }
      })
      const params = {
        data: {
          contacts: map
        }
      }
      try {
        const {data} = await axios.patch('v4/show/company/' + id + '?expand=stands,eventCode,showCode,showTitle,eventTitle,tariff', params)
        commit('setCompany', data.data)
        this.commit('dialog/SetFullDialogShow', false)
        this.commit('alerts/setAlert', {
          show: true,
          color: 'green',
          messages: [i18n.t('company.notifications.contact_deleted')]
        })
        return true
      }catch (e) {
        return false
      }
    }
  },
  async deleteAddressFromServer({commit, state},payload:any){
    if(state.company){
      const id = state.company.id
      const map:any = []
      state.company.locations.forEach((location: any)=>{
        if(location.id != payload) {
          map.push(location)
        }
      })
      state.company.locations = map
      const params = {
        data: {
          locations: map
        }
      }
      try {
        const {data} = await axios.patch('v4/show/company/' + id + '?expand=stands,eventCode,showCode,showTitle,eventTitle,tariff', params)
        commit('setCompany', data.data)
        this.commit('dialog/SetFullDialogShow', false)
        this.commit('alerts/setAlert', {
          show: true,
          color: 'green',
          messages: [i18n.t('company.notifications.contact_deleted')]
        })
        return true
      }catch (e) {
        return false
      }
    }
  },
  async newContactsFromServer({commit, state},payload:any){
    if(state.company){
      const id = state.company.id
      const map:any[] = []
      state.company.contacts.forEach((contact:any)=>{
        if(contact.type != 'address') {
          map.push(contact)
        }
      })
      map.push(payload)
      const params = {
        data: {
          contacts: map
        }
      }
      try {
        const {data} = await axios.patch('v4/show/company/' + id + '?expand=stands,eventCode,showCode,showTitle,eventTitle,tariff', params)
        commit('setCompany', data.data)
        this.commit('dialog/SetFullDialogShow', false)
        this.commit('alerts/setAlert', {
          show: true,
          color: 'green',
          messages: [i18n.t('company.notifications.contact_added')]
        })
        return true
      }catch (e) {
        return false
      }
    }
  },
  async newAddressFromServer({commit, state},payload:any){
    if(state.company){
      const id = state.company.id
      state.company.locations.push(payload)
      const params = {
        data: {
          locations: state.company.locations
        }
      }
      try {
        const {data} = await axios.patch('v4/show/company/' + id + '?expand=stands,eventCode,showCode,showTitle,eventTitle,tariff', params)
        commit('setCompany', data.data)
        this.commit('dialog/SetFullDialogShow', false)
        this.commit('alerts/setAlert', {
          show: true,
          color: 'green',
          messages: [i18n.t('company.notifications.contact_added')]
        })
        return true
      }catch (e) {
        return false
      }
    }
  }
}

export default {
  namespaced: true,
  state: defaultState,
  getters,
  mutations,
  actions
}
